import React from "react";
import { t } from "ttag";
import { CheckedIcon, Spinner, Table } from "@coolremote/shared";
import {
  useCreateModbusFunctionalTemplate,
  useDeleteModbusFunctionalTemplate,
  useGetSiteModbusFunctionalTemplates,
  useRenameModbusFunctionalTemplate,
} from "../../../../hooks/useModbusFunctionalTemplatesApi";
import { IModbusFunctionalTemplate } from "../../types";
import LibraryActionBanner from "./LibraryActionBanner";
import LibraryToolbar from "./LibraryToolbar";
import { buildCheckboxColumn, buildDeleteActionColumn, buildNameColumn } from "./libraryColumns";
import { ILibrarySaveApi, useLibraryTab } from "./useLibraryTab";
import styles from "./ModbusSiteLibraryManagementDialog.module.css";

interface IProps {
  siteId?: string;
  active: boolean;
  /** Lets the dialog drive this tab's Save from its own footer button. */
  onSaveApiChange?: (api: ILibrarySaveApi | null) => void;
}

const FunctionalUnitsLibraryTab: React.FC<IProps> = ({ siteId, active, onSaveApiChange }) => {
  const { mutate: createFunctionalTemplate } = useCreateModbusFunctionalTemplate({});
  const { mutate: renameFunctionalTemplate } = useRenameModbusFunctionalTemplate({});
  const { mutate: deleteFunctionalTemplate } = useDeleteModbusFunctionalTemplate({});
  const { data, isLoading, refetch } = useGetSiteModbusFunctionalTemplates(siteId, active) as unknown as {
    data: IModbusFunctionalTemplate[] | null;
    isLoading: boolean;
    refetch: () => void;
  };

  const lib = useLibraryTab<IModbusFunctionalTemplate>({
    items: data ?? [],
    refetch,
    active,
    siteId,
    create: createFunctionalTemplate,
    rename: (templateId, name) => renameFunctionalTemplate({ templateId, name }),
    remove: deleteFunctionalTemplate,
    pickExportFields: ({ name, rows, defaultUiTemplate, category }) => ({ name, rows, defaultUiTemplate, category }),
    importedItemsLabel: t`units`,
    onSaveApiChange,
  });

  return (
    <div className={styles.tabRoot}>
      <LibraryToolbar
        search={lib.search}
        onSearchChange={lib.setSearch}
        exportMode={lib.exportMode}
        selectedCount={lib.selectedIds.size}
        onEnterExportMode={() => lib.setExportMode(true)}
        onExitExportMode={lib.exitExportMode}
        onExportSelected={lib.handleExportSelected}
        siteId={siteId}
        importing={lib.importing}
        importProgress={lib.importProgress}
        fileInputRef={lib.fileInputRef}
        onFileChange={lib.handleFileChange}
      />
      <LibraryActionBanner banner={lib.banner} />

      <div className={styles.tabBody}>
      {/* Spinner only on the first load — a background refetch (e.g. after a
          rename) keeps the table on screen instead of blanking the tab. */}
      {isLoading && !data ? (
        <div className={styles.loaderWrap}>
          <Spinner size={28} />
        </div>
      ) : (
        <Table
          stickyHeader
          data={lib.filtered}
          getRowKey={(row: IModbusFunctionalTemplate, index: number) => row.id ?? index}
          emptyMessage={lib.search ? t`No templates match your search.` : t`No functional unit templates found for this site.`}
          columns={[
            ...buildCheckboxColumn(lib.exportMode, lib.allSelected, lib.toggleAll, lib.selectedIds, lib.toggleSelected),
            ...buildNameColumn<IModbusFunctionalTemplate>(t`UNIT NAME`, "25%", lib.editingNames, lib.setDraftName, lib.pendingDeleteIds),
            {
              id: "registers",
              title: t`REGISTERS COUNT`,
              width: "15%",
              render: (row: IModbusFunctionalTemplate) => (row.rows?.length ?? 0) + " " + t`registers`,
            },
            {
              id: "parameters",
              title: t`PARAMETERS`,
              width: lib.exportMode ? "35%" : "25%",
              render: (row: IModbusFunctionalTemplate) => (
                <div className={styles.paramList}>{(row.rows ?? []).map((r) => r.name).join(", ")}</div>
              ),
            },
            {
              id: "controlUi",
              title: t`CONTROL UI`,
              width: "10%",
              align: "center" as const,
              render: (row: IModbusFunctionalTemplate) => (row.defaultUiTemplate ? <CheckedIcon width={16} height={16} /> : <span>–</span>),
            },
            ...buildDeleteActionColumn(lib.exportMode, lib.pendingDeleteIds, lib.togglePendingDelete),
          ]}
        />
      )}
      </div>
    </div>
  );
};

export default FunctionalUnitsLibraryTab;
